import {
  Clock,
  Euler,
  Group,
  PerspectiveCamera,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { Stars } from "./objects/stars";
import { MorphParticles, ShapeType } from "./objects/morph-particles";
import { ElectronSwarm } from "./objects/electron-swarm";
import { ElectricArcs } from "./objects/arcs";
import { Style } from "./style";
import { addEase, addEase2 } from "../utils/ease";
import { deviceInfo } from "../utils/deviceinfo";

export class Renderer {
  private startTime = Date.now();
  private style: Style;

  private renderer: WebGLRenderer;
  private electronRenderer: WebGLRenderer;
  private camera: PerspectiveCamera;
  private scene: Scene;
  private electronScene: Scene;
  private canvas: HTMLElement;

  private scrollPercent = 0;
  private targetScrollPercent = 0;
  private lastScrollPercent = 0;
  private scrollVelocity = 0;
  private targetScrollVelocity = 0;
  private targetWidth = 1200; // Width of the current target heading

  private mouse: Vector2;
  private mouseSmooth: Vector2;
  private tilt: Vector2;
  private tiltSmooth: Vector2;
  private width = 0;
  private height = 0;

  private clock = new Clock();
  private delta = 0;
  private interval = 1 / 60;

  private stars: Stars;
  private starsGroup: Group;
  private morphParticles: MorphParticles;
  private electronSwarm: ElectronSwarm;
  private electricArcs: ElectricArcs;

  constructor(style: Style) {
    this.style = style;
    this.canvas = document.getElementById("mainCanvas")!;
    this.width = this.canvas.offsetWidth;
    this.height = this.canvas.offsetHeight;

    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("#logo")) {
        event.preventDefault();
        this.setSpeed(20);
      }
      if (target.closest("#about-logo")) {
        event.preventDefault();
        this.setSpeed(30);
      }
    });

    this.mouse = new Vector2(0, 0);
    this.mouseSmooth = this.mouse;
    this.tilt = new Vector2(0, 0);
    this.tiltSmooth = new Vector2(0, 0);

    this.renderer = new WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.autoClear = false;
    this.renderer.setClearColor(0x000000, 0.0);

    this.canvas?.appendChild(this.renderer.domElement);

    // Create separate renderer for electron swarm (rendered on top)
    this.electronRenderer = new WebGLRenderer({ antialias: true, alpha: true });
    this.electronRenderer.setSize(this.width, this.height);
    this.electronRenderer.setPixelRatio(window.devicePixelRatio);
    this.electronRenderer.setClearColor(0x000000, 0.0);

    const electronCanvas = this.electronRenderer.domElement;
    electronCanvas.id = "electronCanvas";
    electronCanvas.style.position = "fixed";
    electronCanvas.style.top = "0";
    electronCanvas.style.left = "0";
    electronCanvas.style.width = "100%";
    electronCanvas.style.height = "100%";
    electronCanvas.style.pointerEvents = "none";
    electronCanvas.style.zIndex = "999";

    document.body.appendChild(electronCanvas);

    this.camera = new PerspectiveCamera(
      this.style.cameraPerspective,
      this.width / this.height,
      0.1,
      12000,
    );

    this.scene = new Scene();
    this.scene.add(this.camera);

    this.electronScene = new Scene();

    this.starsGroup = new Group();
    this.scene.add(this.starsGroup);

    this.stars = new Stars();
    this.starsGroup.add(this.stars);

    this.morphParticles = new MorphParticles();
    this.morphParticles.position.z = 1500;
    this.scene.add(this.morphParticles);

    this.electronSwarm = new ElectronSwarm();
    this.electronScene.add(this.electronSwarm);

    this.electricArcs = new ElectricArcs();
    this.electronScene.add(this.electricArcs);

    this.render();
  }

  public setSpeed(speed: number = 20): void {
    this.stars.setSpeed(speed);
  }

  public setMobileMenuOpen(isOpen: boolean): void {
    const time = (Date.now() - this.startTime) / 1000;
    this.electronSwarm.setMenuOpen(isOpen, time);
    this.electricArcs.setMenuOpen(isOpen, time);
  }

  public resize(): void {
    if (
      this.width != this.canvas.offsetWidth ||
      this.height != this.canvas.offsetHeight
    ) {
      this.width = this.canvas.offsetWidth;
      this.height = this.canvas.offsetHeight;
      this.renderer.setSize(this.width, this.height);
      this.electronRenderer.setSize(this.width, this.height);
      this.camera.aspect = this.width / this.height;
      this.camera.updateProjectionMatrix();
    }
  }

  public setMouse(mouseX: number, mouseY: number): void {
    this.mouse = new Vector2(mouseX, mouseY);
  }

  public setTargetWidth(width: number): void {
    this.targetWidth = width;
  }

  public setTilt(beta: number, gamma: number): void {
    this.tilt = new Vector2(gamma, beta);
  }

  public setScroll(scrollPercent: number): void {
    this.targetScrollPercent = scrollPercent;
  }

  public setTab(tabId: number): void {
    // Home = 1 -> Beaker (2)
    // About = 2 -> Blockchain (3)
    // Services = 3 -> Sphere (1)
    // Contact = 4 -> Cube (0)
    // Privacy = 5 -> Eth (4)

    let shape = ShapeType.BEAKER;
    switch (tabId) {
      case 1:
        shape = ShapeType.BEAKER;
        break;
      case 2:
        shape = ShapeType.BLOCKCHAIN;
        break;
      case 3:
        shape = ShapeType.SPHERE;
        break;
      case 4:
        shape = ShapeType.NETWORK;
        break;
      case 5:
        shape = ShapeType.ETH;
        break;
    }

    this.morphParticles.transitionToShape(shape, 3);
  }

  public destroy(): void {
    this.renderer.forceContextLoss();
    this.electronRenderer.forceContextLoss();
    const electronCanvas = document.getElementById("electronCanvas");
    if (electronCanvas) {
      electronCanvas.remove();
    }
  }

  private update(): void {
    const time = Date.now() - this.startTime;

    addEase2(this.mouseSmooth, this.mouse, 12);
    addEase2(this.tiltSmooth, this.tilt, 12);

    // Calculate scroll velocity (change in scroll position)
    const scrollDelta = Math.abs(
      this.targetScrollPercent - this.lastScrollPercent,
    );
    this.lastScrollPercent = this.scrollPercent;

    // Smooth scroll velocity with threshold and decay
    this.targetScrollVelocity = scrollDelta * 60; // Scale up for visibility
    this.scrollVelocity = addEase(
      this.scrollVelocity,
      this.targetScrollVelocity,
      15, // Slower response for smoother animation
    );

    // Apply decay when not scrolling
    this.targetScrollVelocity *= 0.85;

    this.scrollPercent = addEase(
      this.scrollPercent,
      this.targetScrollPercent,
      12,
    );

    // Orbit around (0, 0, 1500)
    // Radius = 1500
    const radius = 1500;
    const centerZ = 1500;
    const angle = (this.scrollPercent * Math.PI) / 100; // 0 to PI

    const x = -radius * Math.sin(angle);
    const z = centerZ + radius * Math.cos(angle);

    this.camera.position.set(x, 0, z);
    this.camera.lookAt(0, 0, centerZ);

    // Move stars group to camera Z so rotation is always around the viewer
    this.starsGroup.position.z = z;
    // Offset stars back so they stay in place in the world
    this.stars.position.z = -z;

    // Apply rotation to the group (around camera)
    // this.starsGroup.rotation.x = this.mouseSmooth.y * 0.0002;
    // this.starsGroup.rotation.y = this.mouseSmooth.x * 0.0002;
    // this.starsGroup.rotation.x = (this.tiltSmooth.y * Math.PI) / 180 / 4;
    // this.starsGroup.rotation.y = (this.tiltSmooth.x * Math.PI) / 180 / 4;

    this.stars.update();
    this.morphParticles.update(time / 1000);

    // Calculate target center for electron swarm
    // Camera Right vector: (cos(angle), 0, sin(angle))
    // Camera Up vector: (0, 1, 0)

    // Parallax effect: Scale down movement to simulate depth
    // 1.0 factor makes particles align with the target plane
    const parallaxFactor = 1.0;
    const scale = (2300 / this.height) * parallaxFactor;

    const right = new Vector3(Math.cos(angle), 0, Math.sin(angle));
    const up = new Vector3(0, 1, 0);

    const targetCenter = new Vector3(0, 0, 1500)
      .addScaledVector(right, this.mouseSmooth.x * scale)
      .addScaledVector(up, -this.mouseSmooth.y * scale); // Invert Y

    // Clamp target position to keep within visible bounds (approx +/- 1000 vertical)
    targetCenter.y = Math.max(-1000, Math.min(1000, targetCenter.y));

    // Scale target width from screen pixels to 3D space units
    const scaledWidth = this.targetWidth * scale;

    this.electronSwarm.update(
      time / 1000,
      targetCenter,
      this.scrollVelocity,
      right,
      scaledWidth,
    );

    // Update electric arcs
    this.electricArcs.update(
      time / 1000,
      targetCenter,
      this.scrollVelocity,
      right,
      scaledWidth,
    );
  }

  private render(): void {
    requestAnimationFrame(() => this.render());
    this.delta += this.clock.getDelta();
    if (this.delta > this.interval) {
      this.update();
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
      this.electronRenderer.clear();
      this.electronRenderer.render(this.electronScene, this.camera);
      this.delta = this.delta % this.interval;
    }
  }
}
